import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PenLine, X, Plus } from 'lucide-react';
import api from '../utils/api';

const CATEGORIES = ['신발', '가방', '액세서리', '의류', '생활용품', '스포츠용품', '기타'];

export default function IdeaWritePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', category: '', tags: '', analysis: '' });
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags(prev => [...prev, t]);
    setTagInput('');
  };

  const removeTag = (t) => setTags(prev => prev.filter(x => x !== t));

  const save = async () => {
    if (!form.title.trim()) return;
    if (!form.category) { alert('카테고리를 선택해주세요.'); return; }
    setSaving(true);
    try {
      await api.post('/ideas/manual', {
        title: form.title,
        category: form.category,
        tags: tags.join(', '),
        analysis: form.analysis,
      });
      navigate('/idea-archive');
    } catch (e) {
      alert('저장 실패: ' + (e.response?.data?.message || e.message));
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <PenLine size={20} className="text-brand" />
          <h1 className="text-xl font-bold text-white">직접 작성</h1>
        </div>
        <button onClick={() => navigate('/idea-archive')}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800">
          <X size={14} /> 취소
        </button>
      </div>

      <div className="space-y-4">
        {/* 제목 */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">제품명 / 아이디어 제목 <span className="text-brand">*</span></label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="예: 3D 프린팅 케이블 정리 클립 세트"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600" />
          </div>

          {/* 카테고리 */}
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">카테고리 <span className="text-brand">*</span></label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map(c => (
                <button key={c} onClick={() => setForm(f => ({ ...f, category: f.category === c ? '' : c }))}
                  className={`px-3 py-1 rounded-full text-xs transition-colors ${form.category === c ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* 태그 */}
          <div>
            <label className="text-gray-400 text-xs mb-1.5 block">태그</label>
            <div className="flex gap-2">
              <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="태그 입력 후 Enter"
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand placeholder-gray-600" />
              <button onClick={addTag} className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white text-sm">
                <Plus size={14} />
              </button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map(t => (
                  <span key={t} className="flex items-center gap-1 px-2 py-0.5 bg-gray-800 text-gray-300 rounded-full text-xs">
                    {t}
                    <button onClick={() => removeTag(t)} className="text-gray-600 hover:text-white"><X size={10} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 내용 */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-800">
            <span className="text-white text-sm font-medium">아이디어 내용</span>
            <span className="text-gray-600 text-xs ml-2">(수요층, 특징, 가격대, 경쟁 제품 등 자유롭게)</span>
          </div>
          <textarea value={form.analysis}
            onChange={e => setForm(f => ({ ...f, analysis: e.target.value }))}
            placeholder="아이디어에 대해 자유롭게 작성하세요.&#10;&#10;예시:&#10;- 타겟: 재택근무 직장인, 게이머&#10;- 특징: 모니터 뒤 케이블을 깔끔하게 정리&#10;- 예상 판매가: 5,000 ~ 10,000원&#10;- 경쟁 제품 대비 강점: 커스텀 색상, 책상 두께 맞춤 조절 가능"
            rows={12}
            className="w-full bg-transparent px-4 py-3 text-gray-300 text-sm focus:outline-none placeholder-gray-700 resize-none" />
        </div>

        {/* 저장 */}
        <div className="flex justify-end gap-2">
          <button onClick={() => navigate('/idea-archive')}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white rounded-lg hover:bg-gray-800">
            취소
          </button>
          <button onClick={save} disabled={!form.title.trim() || !form.category || saving}
            className="flex items-center gap-1.5 bg-brand text-white text-sm px-4 py-2 rounded-lg hover:bg-brand/80 disabled:opacity-40">
            <PenLine size={14} /> {saving ? '저장 중...' : '아카이브에 저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
